import { useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { usePresentarQuiz, useQuiz, type CursoDetalle, type ResultadoDelQuiz } from './api';

/**
 * Quiz — presentar el quiz final, y su resultado.
 *
 * ## Las respuestas viven en el estado, no en el DOM
 *
 * El vanilla leía `input[name="q{id}"]:checked` del documento al enviar, y
 * pintaba la opción elegida escribiendo `style.borderColor` a mano. Aquí la
 * respuesta de cada pregunta es un dato; la opción elegida se pinta porque está
 * elegida, no porque alguien se acordó de cambiarle el color.
 *
 * ## La pregunta sin responder, por su nombre
 *
 * Un aviso genérico —«faltan preguntas»— obligaba a recorrer el quiz buscando el
 * hueco. Se conserva lo que el vanilla ya hacía bien: decir cuántas faltan y
 * llevar el foco a la primera.
 */
export function Quiz({ curso, onVolver }: { curso: CursoDetalle; onVolver: () => void }) {
  const quiz = useQuiz(curso.id, true);
  const presentar = usePresentarQuiz();
  const [respuestas, setRespuestas] = useState<Record<number, string>>({});
  const [resultado, setResultado] = useState<ResultadoDelQuiz | null>(null);
  const formulario = useRef<HTMLFormElement>(null);

  if (resultado) {
    return (
      <Resultado
        r={resultado}
        onReintentar={() => { setResultado(null); setRespuestas({}); void quiz.refetch(); }}
        onVolver={onVolver}
      />
    );
  }

  const preguntas = quiz.data ?? [];

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    const faltan = preguntas.filter((p) => !respuestas[p.id]);
    if (faltan.length) {
      Utils.toast(
        `Falta${faltan.length === 1 ? '' : 'n'} ${faltan.length} pregunta${faltan.length === 1 ? '' : 's'} por responder`,
        'warning',
      );
      const primera = formulario.current?.querySelector<HTMLInputElement>(`input[name="q${faltan[0]!.id}"]`);
      primera?.closest('fieldset')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      primera?.focus();
      return;
    }
    presentar.mutate(
      {
        cursoId: curso.id,
        respuestas: preguntas.map((p) => ({ question_id: p.id, selected_key: respuestas[p.id]! })),
      },
      {
        onSuccess: (r) => {
          if (r.passed) Utils.confetti?.();
          setResultado(r);
          window.scrollTo({ top: 0 });
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo enviar el quiz', 'error'),
      },
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onVolver}>
          <i className="bi bi-arrow-left" aria-hidden="true" /> Volver al curso
        </Button>
        <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
          <i className="bi bi-patch-question-fill" aria-hidden="true" /> Quiz final
        </h1>
      </div>

      {quiz.isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : quiz.isError ? (
        <EstadoError err={quiz.error} que="el quiz" onReintentar={() => void quiz.refetch()} onVolver={onVolver} />
      ) : !preguntas.length ? (
        <p className="rounded-md border border-line bg-surface p-6 text-center text-3 text-muted">
          Este curso todavía no tiene preguntas en su quiz.
        </p>
      ) : (
        <form ref={formulario} onSubmit={enviar} className="rounded-md border border-line bg-surface">
          <p className="flex items-center gap-2 border-b border-line px-4 py-3 text-2 text-ink-2">
            <i className="bi bi-info-circle text-brand-ink" aria-hidden="true" />
            {preguntas.length} pregunta{preguntas.length === 1 ? '' : 's'} ·{' '}
            {Object.keys(respuestas).length} respondida{Object.keys(respuestas).length === 1 ? '' : 's'}
          </p>

          <div className="flex flex-col gap-6 p-4">
            {preguntas.map((p, i) => (
              <fieldset key={p.id} className="border-b border-line pb-6 last:border-0 last:pb-0">
                <legend className="mb-3 text-3 font-bold text-brand-ink [overflow-wrap:anywhere]">
                  {i + 1}. {p.question_text}
                </legend>
                <div className="flex flex-col gap-2">
                  {(p.options || []).map((o) => {
                    const elegida = respuestas[p.id] === o.key;
                    return (
                      <label
                        key={o.key}
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-sm border px-3.5 py-2.5 text-3',
                          'focus-within:focus-anillo',
                          elegida ? 'border-brand-ink bg-brand/[.06]' : 'border-line hover:border-brand-ink',
                        )}
                      >
                        <input
                          type="radio"
                          name={`q${p.id}`}
                          value={o.key}
                          checked={elegida}
                          onChange={() => setRespuestas((r) => ({ ...r, [p.id]: o.key }))}
                          className="size-4 shrink-0 accent-brand"
                        />
                        <span className="[overflow-wrap:anywhere]">{o.text}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ))}
          </div>

          <div className="border-t border-line p-4">
            <Button type="submit" block loading={presentar.isPending}>
              <i className="bi bi-send-fill" aria-hidden="true" /> Enviar respuestas
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function Resultado({
  r, onReintentar, onVolver,
}: {
  r: ResultadoDelQuiz;
  onReintentar: () => void;
  onVolver: () => void;
}) {
  return (
    <div className="mx-auto max-w-2xl">
      <section
        role="status"
        className="mb-5 flex flex-col items-center gap-2 rounded-md border border-line bg-surface px-6 py-8 text-center"
      >
        <i
          className={cn('bi text-10 leading-none', r.passed ? 'bi-trophy-fill text-ok-ink' : 'bi-emoji-frown text-muted')}
          aria-hidden="true"
        />
        <p className={cn('text-9 font-extrabold tabular-nums', r.passed ? 'text-ok-ink' : 'text-danger-ink')}>
          {Math.round(r.score)} %
        </p>
        <p className="text-5 font-bold text-brand-ink">
          {r.passed ? 'Aprobado' : 'No aprobado'} — {r.correct} de {r.total} correctas
        </p>
        {!r.passed && (
          <p className="text-2 text-muted">
            Se necesita un {r.passing_score} % para aprobar. Puedes repasar las lecciones y volver a
            intentarlo.
          </p>
        )}
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {r.passed && r.certificate_url ? (
            <a
              href={r.certificate_url}
              target="_blank"
              rel="noopener"
              className="inline-flex min-h-10 items-center gap-2 rounded-sm bg-brand px-6 text-3 font-semibold
                         text-on-brand no-underline hover:bg-brand-strong pointer-coarse:min-h-11 focus-visible:focus-anillo"
            >
              <i className="bi bi-award-fill" aria-hidden="true" /> Descargar certificado
            </a>
          ) : !r.passed ? (
            <Button onClick={onReintentar}>
              <i className="bi bi-arrow-repeat" aria-hidden="true" /> Volver a intentarlo
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onVolver}>
            <i className="bi bi-arrow-left" aria-hidden="true" /> Volver al curso
          </Button>
        </div>
      </section>

      <section aria-labelledby="quiz-detalle" className="rounded-md border border-line bg-surface">
        <h2 id="quiz-detalle" className="flex items-center gap-1.5 border-b border-line px-4 py-3 text-3 font-bold text-brand-ink">
          <i className="bi bi-list-check" aria-hidden="true" /> Detalle
        </h2>
        <ol>
          {r.detail.map((d, i) => (
            <li key={i} className="flex gap-3 border-b border-line px-4 py-3 last:border-0">
              <i
                className={cn(
                  'bi mt-0.5 text-5',
                  d.is_correct ? 'bi-check-circle-fill text-ok-ink' : 'bi-x-circle-fill text-danger-ink',
                )}
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-3 font-semibold [overflow-wrap:anywhere]">
                  {i + 1}. {d.question_text}
                </p>
                <p className={cn('text-2', d.is_correct ? 'text-ok-ink' : 'text-danger-ink')}>
                  {d.is_correct ? 'Correcta' : 'Incorrecta'} · tu respuesta:{' '}
                  {d.selected_text || String(d.selected_key || '').toUpperCase()}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
